import { asDate } from "@@/shared/date-time/as-date";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixFactory,
} from "@@/shared/date-time/iso-and-unix";
import { addDays, differenceInDays, startOfDay } from "date-fns";

/**
 * Calculates and returns an array of dates between two specified dates, inclusive.
 * If the start date is after the end date, the dates are swapped to ensure a proper range.
 *
 * @param {IsoAndUnixTimestamp} start - The start date of the range.
 * @param {IsoAndUnixTimestamp} end - The end date of the range.
 * @returns {IsoAndUnixTimestamp[]} An array of Date objects representing each day in the range from the start date to the end date, inclusive.
 *
 * @example
 * If the dates are 2023-01-01 and 2023-01-05
 * returns [2023-01-01, 2023-01-02, 2023-01-03, 2023-01-04, 2023-01-05]
 */
const datesBetweenIso = (
    start: IsoAndUnixTimestamp,
    end: IsoAndUnixTimestamp,
) => {
    return datesBetween(asDate(start), asDate(end)).map(isoAndUnixFactory);
};
/**
 * Calculates and returns an array of dates between two specified dates, inclusive.
 * If the start date is after the end date, the dates are swapped to ensure a proper range.
 *
 * @param {Date} start - The start date of the range.
 * @param {Date} end - The end date of the range.
 * @returns {Date[]} An array of Date objects representing each day in the range from the start date to the end date, inclusive.
 *
 * @example
 * If the dates are 2023-01-01 and 2023-01-05
 * returns [2023-01-01, 2023-01-02, 2023-01-03, 2023-01-04, 2023-01-05]
 */
const datesBetween = (start: Date, end: Date) => {
    const [s, e] = start > end ? [end, start] : [start, end];
    const days = differenceInDays(startOfDay(e), startOfDay(s));
    return [...Array(days + 1).keys()].map(i => addDays(s, i));
};

export { datesBetween, datesBetweenIso };
